import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {FormCheck} from 'react-bootstrap';
import {PresenterProps, withPresenter} from '../../../Support/with_presenter';
import {CrewMembersPresenter} from './CrewMembersPresenter';
import {NetworkRequestStatusView} from '../../Components/Utils/NetworkRequestStatusView';
import {FullCrew} from '../../../Models/Crew';
import {Feedback} from '../../Components/Feedback';
import {Loader} from '../../Components/Utils/Loader';
import {Link} from 'react-router-dom';
import {ErrorPlaceholder} from '../../Components/Utils/ErrorPlaceholder';

interface OwnProps {
    id: number;
    name: string | null;
}

const CrewsMembersComponent: FunctionComponent<OwnProps & PresenterProps<CrewMembersPresenter>> = observer(
    ({id, presenter}) => {
        if (presenter.name === null) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <Loader />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <NetworkRequestStatusView<FullCrew>
                result={presenter.crew}
                loader={() => {
                    return (
                        <div className="container">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to={'/crews/' + id} className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    <h1>{presenter.name}</h1>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-lg-9">
                                    <Loader />
                                </div>
                                <div className="col-12 col-lg-3"></div>
                            </div>
                        </div>
                    );
                }}
                error={() => {
                    return (
                        <div className="container">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to={'/crews/' + id} className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    <h1>{presenter.name}</h1>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-lg-9">
                                    <ErrorPlaceholder large />
                                </div>
                                <div className="col-12 col-lg-3"></div>
                            </div>
                        </div>
                    );
                }}
                component={(crew) => {
                    return (
                        <div className="container">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to={'/crews/' + crew.id} className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    <h1>{crew.name}</h1>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-lg-9">
                                    <div className="row">
                                        <div className="col-xs-12">
                                            <h3 className="mb-2">Members</h3>
                                        </div>
                                    </div>
                                    <div className="card">
                                        <div className="row">
                                            <div className="col-12">
                                                <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th>Name</th>
                                                            <th className="text-end">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {crew.members.map((member) => {
                                                            return (
                                                                <tr>
                                                                    <td>
                                                                        {member.firstName} {member.lastName}
                                                                        {member.hasAcceptedInvite && (
                                                                            <span className="d-inline-block ms-2 badge rounded-pill bg-info">
                                                                                Active
                                                                            </span>
                                                                        )}
                                                                        {!member.hasAcceptedInvite && (
                                                                            <span className="d-inline-block ms-2 badge rounded-pill bg-black">
                                                                                Invited
                                                                            </span>
                                                                        )}
                                                                        {member.isCoach && (
                                                                            <span className="d-inline-block ms-2 badge rounded-pill bg-secondary">
                                                                                Coach
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        <FormCheck
                                                                            type={'switch'}
                                                                            label={`Coach`}
                                                                            reverse
                                                                            role="button"
                                                                            className="d-inline-block me-2"
                                                                            checked={member.isCoach}
                                                                            onChange={() =>
                                                                                presenter.toggleCoach(member)
                                                                            }
                                                                        />
                                                                        <button
                                                                            className="btn btn-sm btn-danger"
                                                                            onClick={() => {
                                                                                if (
                                                                                    confirm(
                                                                                        member.isMe
                                                                                            ? 'Are you sure you want to leave this crew?'
                                                                                            : 'Are you sure you want to remove this member?',
                                                                                    )
                                                                                ) {
                                                                                    presenter.removeMember(member);
                                                                                }
                                                                            }}
                                                                        >
                                                                            {member.isMe ? 'Leave' : 'Remove'}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>

                                                <div className="card-body">
                                                    {crew.members.length < crew.maxMemberCount && (
                                                        <a
                                                            className="btn btn-primary"
                                                            href={'/crews/' + crew.id + '/members/invite'}
                                                        >
                                                            Invite members
                                                        </a>
                                                    )}
                                                    <div className="text-center">
                                                        {crew.members.length >= crew.maxMemberCount && (
                                                            <p>
                                                                <i>
                                                                    You've reached the limit of your subscription
                                                                    package.
                                                                    {presenter.isSubscriptionOwner ? (
                                                                        <> Upgrade your package to grow your crew.</>
                                                                    ) : (
                                                                        <>
                                                                            {' '}
                                                                            Contact the subscription owner to upgrade
                                                                            your package and grow your crew.
                                                                        </>
                                                                    )}
                                                                </i>
                                                            </p>
                                                        )}
                                                        {presenter.isSubscriptionOwner && (
                                                            <>
                                                                <a
                                                                    href={`/account/subscriptions/${presenter.subscriptionId}/change`}
                                                                    className="btn btn-primary align-right"
                                                                >
                                                                    Upgrade your package
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-lg-3">
                                    <Feedback className="mb-3" />
                                </div>
                            </div>
                        </div>
                    );
                }}
            />
        );
    },
);

export const CrewMembers = withPresenter<CrewMembersPresenter, OwnProps>(
    (props, component) => new CrewMembersPresenter(props.id, props.name, component.network.crews, component.network.me),
    CrewsMembersComponent,
);
